// FILE: src/components/ContentList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listContent, deleteContentWithStorage } from "./content";
import { useAuth } from "../context/AuthContext";

/* =========================
   URL + TYPE HELPERS
========================= */

function safeUrl(value) {
  try {
    if (!value) return "";
    const u = new URL(value);
    return u.toString();
  } catch {
    return "";
  }
}

function isYouTubeUrl(url) {
  const u = safeUrl(url);
  if (!u) return false;
  try {
    const parsed = new URL(u);
    const host = parsed.hostname.replace("www.", "");
    return (
      host === "youtube.com" ||
      host === "m.youtube.com" ||
      host === "youtu.be" ||
      host === "youtube-nocookie.com"
    );
  } catch {
    return false;
  }
}

function getYouTubeId(url) {
  const u = safeUrl(url);
  if (!u) return "";

  try {
    const parsed = new URL(u);
    const host = parsed.hostname.replace("www.", "");

    // youtu.be/VIDEO_ID
    if (host === "youtu.be") {
      const id = parsed.pathname.replace("/", "").trim();
      return id || "";
    }

    // youtube.com/watch?v=VIDEO_ID
    const v = parsed.searchParams.get("v");
    if (v) return v;

    // youtube.com/embed/VIDEO_ID
    const parts = parsed.pathname.split("/").filter(Boolean);
    const embedIndex = parts.indexOf("embed");
    if (embedIndex >= 0 && parts[embedIndex + 1]) return parts[embedIndex + 1];

    // youtube.com/shorts/VIDEO_ID
    const shortsIndex = parts.indexOf("shorts");
    if (shortsIndex >= 0 && parts[shortsIndex + 1]) return parts[shortsIndex + 1];

    return "";
  } catch {
    return "";
  }
}

function toYouTubeEmbedUrl(url) {
  const id = getYouTubeId(url);
  if (!id) return "";
  // Use nocookie domain (nice privacy default)
  return `https://www.youtube-nocookie.com/embed/${id}`;
}

function inferTypeFromUrl(url) {
  const u = safeUrl(url);
  if (!u) return { kind: "link", ext: "" };

  // ✅ special case: YouTube links should preview as embedded video
  if (isYouTubeUrl(u)) return { kind: "youtube", ext: "" };

  const clean = u.split("?")[0].toLowerCase();
  const ext = clean.includes(".") ? clean.split(".").pop() : "";

  // video files
  if (["mp4", "webm", "ogg", "mov"].includes(ext)) return { kind: "video", ext };
  // audio files
  if (["mp3", "wav", "ogg", "m4a"].includes(ext)) return { kind: "audio", ext };
  // image files
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) return { kind: "image", ext };
  // pdf
  if (ext === "pdf") return { kind: "pdf", ext };

  return { kind: "link", ext };
}

function inferType(item) {
  const type = String(item?.type || "").toLowerCase().trim();
  const mime = String(item?.mimeType || "").toLowerCase().trim();

  // If your DB explicitly marks it as youtube/link, respect it
  if (type === "youtube") return "youtube";

  // ✅ if fileUrl is a YouTube link, preview as youtube
  if (isYouTubeUrl(item?.fileUrl)) return "youtube";

  if (mime.startsWith("video/") || type === "video") return "video";
  if (mime.startsWith("audio/") || type === "audio") return "audio";
  if (mime.startsWith("image/") || type === "image") return "image";
  if (mime === "application/pdf" || type === "pdf") return "pdf";

  return inferTypeFromUrl(item?.fileUrl).kind;
}

function formatDate(ts) {
  try {
    if (!ts) return "";
    if (ts.toDate) return ts.toDate().toLocaleDateString();
    return new Date(ts).toLocaleDateString();
  } catch {
    return "";
  }
}

/* =========================
   PREVIEW BODY
========================= */

function PreviewBody({ item }) {
  const url = safeUrl(item?.fileUrl);
  if (!url) {
    return <div className="text-sm text-slate-600">No file link found.</div>;
  }

  const kind = inferType(item);

  // ✅ YouTube embed preview
  if (kind === "youtube") {
    const embed = toYouTubeEmbedUrl(url);
    if (!embed) {
      return (
        <div className="text-sm text-slate-600">
          Could not detect a YouTube video ID. Use <span className="font-semibold">Open</span>.
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="w-full rounded-xl border bg-black overflow-hidden">
          <iframe
            title={item?.title || "YouTube Video"}
            src={embed}
            className="w-full aspect-video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
        <div className="text-xs text-slate-500">
          Tip: If the player is blocked on some networks, use “Open”.
        </div>
      </div>
    );
  }

  if (kind === "video") {
    return (
      <video
        src={url}
        controls
        playsInline
        className="w-full rounded-xl border bg-black"
      />
    );
  }

  if (kind === "audio") {
    return <audio src={url} controls className="w-full" />;
  }

  if (kind === "image") {
    return (
      <img
        src={url}
        alt={item?.title || "Content"}
        className="w-full max-h-[70vh] object-contain rounded-xl border bg-white"
        loading="lazy"
      />
    );
  }

  if (kind === "pdf") {
    return (
      <div className="space-y-2">
        <iframe
          title={item?.title || "PDF Preview"}
          src={url}
          className="w-full h-[70vh] rounded-xl border bg-white"
        />
        <div className="text-xs text-slate-500">
          If the PDF does not display here, use “Open”.
        </div>
      </div>
    );
  }

  return (
    <div className="text-sm text-slate-600">
      Preview not available for this file type. Use{" "}
      <span className="font-semibold">Open</span>.
    </div>
  );
}

/* =========================
   MAIN LIST
========================= */

export default function ContentList() {
  const navigate = useNavigate();
  const { user, role, tenantConfig } = useAuth();

  const enabledMenus = tenantConfig?.enabledMenus ?? [];
  const canUpload =
    user && role === "admin" && enabledMenus.includes("admin.uploadContent");
  const canManageContent = user && role === "admin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [search, setSearch] = useState("");

  // preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listContent();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load content:", e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;

    return items.filter((c) => {
      const title = String(c.title || "").toLowerCase();
      const desc = String(c.description || "").toLowerCase();
      const type = String(c.type || "").toLowerCase();
      return title.includes(term) || desc.includes(term) || type.includes(term);
    });
  }, [items, search]);

  const openPreview = (item) => {
    setPreviewItem(item);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewItem(null);
  };

  const onDelete = async (item) => {
    if (!item?.id) return;
    if (!window.confirm(`Delete "${item.title || "this content"}"?`)) return;

    try {
      setBusyId(item.id);
      await deleteContentWithStorage(item.id);
      await load();
    } catch (e) {
      console.error(e);
      alert(e?.message || "Delete failed.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="max-w-6xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Content</h2>
            <p className="text-sm text-slate-600 mt-1">
              Watch videos, read PDFs, and download resources.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search content…"
              className="w-full sm:w-72 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />

            {canUpload && (
              <button
                onClick={() => navigate("/admin/content/upload")}
                className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
              >
                + Upload Content
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="bg-white border rounded-2xl p-6 text-sm text-slate-600">
            Loading content…
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border rounded-2xl p-6 text-sm text-slate-600">
            No content found.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((c) => {
              const url = safeUrl(c.fileUrl);
              const kind = inferType(c);
              const dateLabel = formatDate(c.createdAt);

              const badge =
                kind === "youtube"
                  ? "YouTube"
                  : kind === "video"
                  ? "Video"
                  : kind === "audio"
                  ? "Audio"
                  : kind === "pdf"
                  ? "PDF"
                  : kind === "image"
                  ? "Image"
                  : "Link";

              const ytEmbed = kind === "youtube" && url ? toYouTubeEmbedUrl(url) : "";

              return (
                <div
                  key={c.id}
                  className="bg-white border rounded-2xl p-4 shadow-sm hover:shadow-md transition"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900 truncate">
                          {c.title || "Untitled"}
                        </h3>
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                          {badge}
                        </span>
                      </div>

                      {c.description && (
                        <p className="mt-1 text-sm text-slate-600 line-clamp-2">
                          {c.description}
                        </p>
                      )}

                      {dateLabel && (
                        <div className="mt-2 text-xs text-slate-500">
                          Added: {dateLabel}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Mini preview */}
                  <div className="mt-3">
                    {kind === "youtube" && ytEmbed ? (
                      <div className="rounded-xl border overflow-hidden bg-black">
                        <iframe
                          title={c.title || "YouTube Preview"}
                          src={ytEmbed}
                          className="w-full aspect-video"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      </div>
                    ) : kind === "video" && url ? (
                      <div className="rounded-xl border overflow-hidden bg-black">
                        <video
                          src={url}
                          controls
                          playsInline
                          className="w-full h-40 object-cover"
                        />
                      </div>
                    ) : kind === "image" && url ? (
                      <div className="rounded-xl border overflow-hidden bg-white">
                        <img
                          src={url}
                          alt={c.title || "Preview"}
                          className="w-full h-40 object-cover"
                          loading="lazy"
                        />
                      </div>
                    ) : (
                      <div className="h-40 rounded-xl border bg-slate-50 flex items-center justify-center text-sm text-slate-500">
                        Preview available in viewer
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      onClick={() => openPreview(c)}
                      className="px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 text-sm border border-indigo-100 hover:bg-indigo-100"
                    >
                      Preview
                    </button>

                    {url && (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-1.5 rounded-lg bg-white text-slate-700 text-sm border hover:bg-slate-50"
                      >
                        Open
                      </a>
                    )}

                    {canManageContent && (
                      <button
                        onClick={() => onDelete(c)}
                        disabled={busyId === c.id}
                        className="ml-auto px-3 py-1.5 rounded-lg bg-white text-red-600 text-sm border border-red-200 hover:bg-red-50 disabled:opacity-60"
                      >
                        {busyId === c.id ? "Deleting…" : "Delete"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Preview modal */}
      {previewOpen && previewItem && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl border overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">
                  {previewItem.title || "Preview"}
                </div>
                {previewItem.description && (
                  <div className="text-xs text-slate-500 truncate">
                    {previewItem.description}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {safeUrl(previewItem.fileUrl) && (
                  <a
                    href={safeUrl(previewItem.fileUrl)}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-1.5 rounded-lg bg-white text-slate-700 text-sm border hover:bg-slate-50"
                  >
                    Open
                  </a>
                )}
                <button
                  onClick={closePreview}
                  className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="p-4">
              <PreviewBody item={previewItem} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}